import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './CampaignList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/campaigns/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { campaignActions } from '~/thunks/campaign/campaignSlice';
import { ICampaign, ICampaignList } from '~/utils/interface/campaign';
import { PERMISSIONS } from '~/utils/constants/permission';
import { STATUS_SCHEDULED, STATUS_SENT } from '~/utils/constants/campaign';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

type CampaignListProps = {
  data?: ICampaign;
  onOpenEdit: (id: string) => void;
};

function CampaignList(props: CampaignListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const permissions = usePermission();
  const { t } = useTranslation(['admin/campaign']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { campaigns, filter, currentPage, totalPages } = useAppSelector((state) => state.campaign);
  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('properties.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'status',
        label: t('properties.status'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'sendAt',
        label: t('sendDate'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'subject',
        label: t('properties.subject'),
        sortable: true,
        searchable: false,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(campaignActions.changeCampaignId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(campaignActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(campaignActions.changeMenuSelectId(menuSelectedId));
      dispatch(campaignActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      campaignActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      campaignActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  //#endregion Handle Function

  return (
    <Box className={cx('campaign-list-container')}>
      <BaseTable<ICampaignList>
        data={campaigns}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(campaignItem: ICampaignList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                {campaignItem.name}
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('statuses', campaignItem.status)}>{t(`statuses.${campaignItem.status}`)}</Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                {campaignItem.status === STATUS_SENT &&
                  campaignItem.sendAt &&
                  `${t('sentAt')} ${dayjs(campaignItem.sendAt).format('MM/DD/YYYY hh:mm A')}`}
                {campaignItem.status === STATUS_SCHEDULED &&
                  campaignItem.sendAt &&
                  `${t('scheduledAt')} ${dayjs(campaignItem.sendAt).format('MM/DD/YYYY hh:mm A')}`}
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                {campaignItem.subject}
              </TableCell>
              {(permissions[PERMISSIONS.campaign_delete] || permissions[PERMISSIONS.campaign_edit]) && (
                <TableCell>
                  <Box className={'flex justify-end'}>
                    <Box className={cx('campaign-container')}>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, campaignItem.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(CampaignList);
